import {
  ErrorHandler,
  inject,
  Injector,
  LOCALE_ID,
  NgModule,
  provideAppInitializer,
  isDevMode
} from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { Locales, LocaleService } from "src/app/services/locale.service";
import { TEMPLATE_APPOINTMENT_REMINDER_TOKEN } from "src/app/tokens/template_appointment_reminder.token";
import { AppRoutingModule } from "./routing/app-routing.module";
import { AppComponent } from "./app.component";
import {
  provideHttpClient,
  withFetch,
  withInterceptors
} from "@angular/common/http";
import { dynamicInterceptorBridgeFn } from "./interceptors/dynamic-interceptor-bridge";
import { TranslocoRootModule } from "./transloco/transloco-root.module";
import { GuardsModule } from "./guards/guards.module";
import { Amplify } from "aws-amplify";
import { environment } from "../environments/environment";
import { InterceptorsModule } from "./interceptors/interceptors.module";
import { ServicesModule } from "./services/services.module";
import { BackendModule } from "./backend/backend.module";
import { CreateEntityModule } from "./components/ui/create-entity/create-entity.module";
import { BootstrapService } from "./modules/bootstrap/bootstrap.service";
import { BoxService } from "./services/box.service";
import { RocketBackendModule } from "./modules/bootstrap/rocket-ui/backend/rocket-backend.module";
import { TranslocoService } from "@jsverse/transloco";
import { setTranslator } from "./support/translate";
import { Router, RouteReuseStrategy } from "@angular/router";
import { AppRouterReuseStrategy } from "./routing/app-router-reuse-strategy";
import { ProductService } from "./services/product.service";
import { ServiceWorkerModule } from "@angular/service-worker";
import { registerLocaleData } from "@angular/common";
import localeIt from "@angular/common/locales/it";
import localeEs from "@angular/common/locales/es";
import localeFr from "@angular/common/locales/fr";
import localeKa from "@angular/common/locales/ka";
import { Ability, PureAbility } from "@casl/ability";
import * as Sentry from "@sentry/angular";
import { EventsModule } from "src/app/events/events.module";
import { EventsService } from "src/app/events/events.service";
import { ConfirmService } from "src/app/services/confirm.service";
import { PRODUCT_TOKEN } from "src/app/core/product.token";
import { AUTH_CONFIGURATION } from "src/app/tokens/auth-configuration.token";
import { BrokerModule } from "src/app/sse/broker.module";
import { BrokerService } from "src/app/sse/broker.service";

registerLocaleData(localeIt);
registerLocaleData(localeEs);
registerLocaleData(localeFr);
registerLocaleData(localeKa);

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrokerModule,
    BrowserModule,
    AppRoutingModule,
    TranslocoRootModule,
    GuardsModule,
    InterceptorsModule,
    ServicesModule,
    BackendModule,
    RocketBackendModule,
    CreateEntityModule,
    EventsModule,
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: "registerWhenStable:30000"
    })
  ],
  providers: [
    {
      provide: PRODUCT_TOKEN,
      useFactory: () => {
        return inject(ProductService).getCurrentProduct();
      }
    },
    provideHttpClient(
      withInterceptors([dynamicInterceptorBridgeFn]),
      withFetch()
    ),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler()
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    { provide: LOCALE_ID, useValue: "it" },
    ProductService,
    ConfirmService,
    BootstrapService,
    BoxService,
    AppRouterReuseStrategy,
    {
      provide: RouteReuseStrategy,
      deps: [AppRouterReuseStrategy],
      useFactory: (strategy: AppRouterReuseStrategy) => strategy
    },
    { provide: Ability, useValue: new Ability() },
    { provide: PureAbility, useExisting: Ability },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
      inject(BrokerService);
    }),
    {
      provide: TEMPLATE_APPOINTMENT_REMINDER_TOKEN,
      useFactory: () => {
        const localeService = inject(LocaleService);
        return () => {
          const currentLocale = localeService.getCurrent();
          switch (currentLocale) {
            case Locales.FR:
            case Locales.ES:
            case Locales.EN:
            case Locales.KA: {
              return environment.snoots.templates.appointment_reminder_en;
            }
            case Locales.IT: {
              return environment.snoots.templates.appointment_reminder_it;
            }
          }
        };
      }
    }
  ]
})
export class AppModule {
  static Injector: Injector;
  private readonly translocoService = inject(TranslocoService);
  private readonly eventsService = inject(EventsService);

  constructor() {
    AppModule.Injector = inject(Injector);

    this.eventsService.register();

    setTranslator(this.translocoService);

    Amplify.configure({
      Auth: {
        region: inject(AUTH_CONFIGURATION).region,
        userPoolId: inject(AUTH_CONFIGURATION).userPoolId,
        userPoolWebClientId: inject(AUTH_CONFIGURATION).userPoolWebClientId
      }
    });
  }
}
